import {
    AuthInfo,
    ModeInfo,
    SignDoc,
    SignerInfo,
    SignMode,
    SimplePublicKey,
} from '@terra-money/terra.js';
import { chainID, lcdClient } from '../lcd';

export const KEPLR_IDENTIFIER = 'keplr';

/**
 * Keplr injects the same object in its mobile in-app browser and in the desktop
 * extension, and reports which through `mode`.
 */
export function getKeplr() {
    return typeof window === 'undefined' ? undefined : window.keplr;
}

export function isKeplrAvailable() {
    return Boolean(getKeplr());
}

/** True only inside the Keplr app's own browser, where it is the only wallet. */
export function isKeplrInAppBrowser() {
    return getKeplr()?.mode === 'mobile-web';
}

/**
 * Terra Classic as wallet-provider expects to see it.
 *
 * getChainOptions() only reads the `mainnet` section of the chain registry,
 * which holds phoenix-1 and osmosis-1 — columbus-5 lives in a separate
 * `classic` section it never looks at. So the provider's default network has no
 * Terra Classic in it and this has to be supplied by the session.
 *
 * `name` is 'Terra Classic' rather than the registry's shorter 'Classic'
 * because CheckChainType compares against that exact string. `lcd` points at
 * the same endpoint as the rest of the app (see src/lcd.js) so a page reading
 * the wallet's network cannot end up querying a different node than the pages.
 */
const TERRA_CLASSIC_NETWORK = {
    [chainID]: {
        chainID,
        name: 'Terra Classic',
        lcd: 'https://terra-classic-lcd.publicnode.com',
        prefix: 'terra',
        baseAsset: 'uluna',
        coinType: '330',
        gasAdjustment: 1.5,
        gasPrices: { uluna: 28.325 },
        isClassic: true,
    },
};

/** Only used if Keplr does not already know columbus-5. */
const SUGGEST_CHAIN_INFO = {
    chainId: chainID,
    chainName: 'Terra Classic',
    rpc: 'https://terra-classic-rpc.publicnode.com',
    rest: 'https://terra-classic-lcd.publicnode.com',
    bip44: { coinType: 330 },
    bech32Config: {
        bech32PrefixAccAddr: 'terra',
        bech32PrefixAccPub: 'terrapub',
        bech32PrefixValAddr: 'terravaloper',
        bech32PrefixValPub: 'terravaloperpub',
        bech32PrefixConsAddr: 'terravalcons',
        bech32PrefixConsPub: 'terravalconspub',
    },
    currencies: [
        { coinDenom: 'LUNC', coinMinimalDenom: 'uluna', coinDecimals: 6 },
        { coinDenom: 'USTC', coinMinimalDenom: 'uusd', coinDecimals: 6 },
    ],
    feeCurrencies: [
        {
            coinDenom: 'LUNC',
            coinMinimalDenom: 'uluna',
            coinDecimals: 6,
            gasPriceStep: { low: 28.325, average: 28.325, high: 50 },
        },
    ],
    stakeCurrency: { coinDenom: 'LUNC', coinMinimalDenom: 'uluna', coinDecimals: 6 },
    features: ['ibc-transfer', 'ibc-go'],
};

async function enableChain(keplr) {
    try {
        await keplr.enable(chainID);
    } catch (err) {
        // Keplr ships Terra Classic, but a stripped build or an old version may
        // not have it. Offer the chain once, then retry.
        if (typeof keplr.experimentalSuggestChain !== 'function') throw err;
        await keplr.experimentalSuggestChain(SUGGEST_CHAIN_INFO);
        await keplr.enable(chainID);
    }
}

// ---------------------------------------------------------------- protobuf --

/**
 * TxRaw is three length-delimited fields, so it is encoded here by hand rather
 * than by importing @terra-money/terra.proto. That package is a transitive
 * dependency of terra.js and is not resolvable from src/ — the same resolution
 * gap that broke the jax import (see src/stubs/terra-proto-jax-tx.js).
 *
 *   message TxRaw {
 *     bytes body_bytes      = 1;
 *     bytes auth_info_bytes = 2;
 *     repeated bytes signatures = 3;
 *   }
 */
function varint(value) {
    const out = [];
    let v = value;
    do {
        let byte = v & 0x7f;
        v = Math.floor(v / 128);
        if (v > 0) byte |= 0x80;
        out.push(byte);
    } while (v > 0);
    return Uint8Array.from(out);
}

function encodeTxRaw(bodyBytes, authInfoBytes, signatures) {
    const chunks = [];
    const field = (fieldNumber, bytes) => {
        chunks.push(varint((fieldNumber << 3) | 2), varint(bytes.length), bytes);
    };
    field(1, bodyBytes);
    field(2, authInfoBytes);
    signatures.forEach(signature => field(3, signature));

    const out = new Uint8Array(chunks.reduce((n, c) => n + c.length, 0));
    let offset = 0;
    for (const chunk of chunks) {
        out.set(chunk, offset);
        offset += chunk.length;
    }
    return out;
}

// -------------------------------------------------------------------- post --

/**
 * Signs with Keplr and broadcasts, matching what this chain already accepts.
 *
 * Every execution of these contracts on chain is SIGN_MODE_DIRECT over
 * /cosmwasm.wasm.v1.MsgExecuteContract, so this signs direct rather than amino:
 * the protobuf encoding terra.js produces is the one already proven to verify
 * here, while the amino type string is not exercised by anything in production.
 */
async function post(txOptions) {
    const keplr = getKeplr();
    if (!keplr) throw new Error('Keplr is not available in this browser.');

    await enableChain(keplr);

    const key = await keplr.getKey(chainID);
    const address = key.bech32Address;
    const publicKey = new SimplePublicKey(Buffer.from(key.pubKey).toString('base64'));

    const account = await lcdClient.auth.accountInfo(address);
    const accountNumber = account.getAccountNumber();
    const sequence = account.getSequenceNumber();

    const unsignedTx = await lcdClient.tx.create([{ address, sequence, publicKey }], txOptions);

    // For direct signing the signed payload must already carry the signer info;
    // terra.js's own Key.createSignature does exactly this before signing.
    const signDoc = new SignDoc(
        chainID,
        accountNumber,
        sequence,
        new AuthInfo(
            [new SignerInfo(publicKey, sequence, new ModeInfo(new ModeInfo.Single(SignMode.SIGN_MODE_DIRECT)))],
            unsignedTx.auth_info.fee,
        ),
        unsignedTx.body,
    );

    const proto = signDoc.toProto(true);
    const { signed, signature } = await keplr.signDirect(chainID, address, {
        bodyBytes: proto.bodyBytes,
        authInfoBytes: proto.authInfoBytes,
        chainId: proto.chainId,
        accountNumber: proto.accountNumber,
    });

    // Broadcast the bytes Keplr actually signed, not the ones we proposed —
    // it is allowed to adjust the fee, and re-encoding ours would invalidate
    // the signature.
    const txBytes = encodeTxRaw(signed.bodyBytes, signed.authInfoBytes, [
        Uint8Array.from(Buffer.from(signature.signature, 'base64')),
    ]);

    const { tx_response: res } = await lcdClient.apiRequester.post('/cosmos/tx/v1beta1/txs', {
        tx_bytes: Buffer.from(txBytes).toString('base64'),
        mode: 'BROADCAST_MODE_SYNC',
    });

    if (res.code) {
        throw new Error(res.raw_log || `Transaction failed with code ${res.code}`);
    }

    return {
        ...txOptions,
        result: { height: Number(res.height), raw_log: res.raw_log, txhash: res.txhash },
        success: true,
    };
}

// ------------------------------------------------------------------ plugin --

// Inlined so the connect dialog needs no network request and nothing is
// hotlinked from Keplr's servers.
const ICON =
    'data:image/svg+xml;utf8,' +
    encodeURIComponent(
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">' +
            '<rect width="48" height="48" rx="12" fill="#1B1B32"/>' +
            '<path d="M17 12h5v10.6L31.4 12H38L27.3 24.1 38 36h-6.8l-9.2-10.7V36h-5z" fill="#F5F5FF"/>' +
            '</svg>',
    );

export const keplrWalletPlugin = {
    name: 'Keplr',
    type: 'KEPLR',
    identifier: KEPLR_IDENTIFIER,
    icon: ICON,

    // `networks` arrives empty: the controller derives it from
    // walletConnectChainIds, which getChainOptions() hardcodes to {}.
    createSession: async () => {
        const keplr = getKeplr();
        if (!keplr) return null;

        const session = {
            network: TERRA_CLASSIC_NETWORK,
            addresses: null,

            connect: async () => {
                await enableChain(keplr);
                const key = await keplr.getKey(chainID);
                session.addresses = { [chainID]: key.bech32Address };
            },

            disconnect: async () => {
                session.addresses = null;
            },

            getMetadata: () => ({ wallet: 'keplr', mode: keplr.mode, name: keplr.mode }),

            post,
        };

        return session;
    },
};
